import React from 'react'
import { isEntitled } from '@classic/Betting-v2/Services/EntitlementService'
import { OverlayOpen } from '@core/Components/Overlay'
import { GiddyUpPage } from '@core/Areas/Racing/Components/RacingForm/GiddyUpPage'
import { showErrorMessage, showWarningMessage } from '@classic/Betting-v2/Utils/ErrorHandler'
import { showLogin } from '@core/Areas/Login/helpers'

export function openGiddyUp(
  raceKey: string,
  fixtureKey: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getStarterFunc: (starterNumber: number) => any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRaceDetailFunc: () => any,
  selectAcceptorFunc: (acceptorNumber: number, isFixed: boolean) => void,
  clearSelectionFunc: () => void,
  selectQuaddieFunc: (raceNumbers: number[], acceptorNumbers: number[][]) => void
) {
  const promise = isEntitled('giddyUp')
  promise
    .then(result => {
      if (result != undefined) {
        if (result.success && result.data.isGiddyUpEntitled) {
          displayGiddyUpForm(
            raceKey,
            fixtureKey,
            getStarterFunc,
            getRaceDetailFunc,
            selectAcceptorFunc,
            clearSelectionFunc,
            selectQuaddieFunc
          )
        } else if (result.errorCode === 403) {
          showLogin()
        } else {
          showWarningMessage('', result.data.reasonText)
        }
      }
    })
    .catch(() => {
      const message =
        'If you require assistance please contact our Customer Service Centre on 1300 36 36 88'
      showErrorMessage('Service is currently unavailable', message)
    })
}

// Local Helpers

function displayGiddyUpForm(
  giddyUpRaceKey: string,
  giddyUpFixtureKey: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getStarterFunc: (starterNumber: number) => any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRaceDetailFunc: () => any,
  selectAcceptorFunc: (acceptorNumber: number, isFixed: boolean) => void,
  clearSelectionFunc: () => void,
  selectQuaddieFunc: (raceNumbers: number[], acceptorNumbers: number[][]) => void
): void {
  const raceInfo = {
    raceKey: giddyUpRaceKey,
    fixtureKey: giddyUpFixtureKey,
    getAcceptor: (starterKey: string) => {
      let result: Record<string, unknown> = {}

      // starterKey e.g. '562439-1'
      const [, starterNumber] = starterKey.split('-')
      // TD: Note: Assumes currently viewed race.  When multiple races will need to
      // refactor mobi to collect race-ids and/or starter ids, OR initiate giddyup js earlier
      // and have it collect the data for races/meetings.
      if (!starterNumber) return defaultResult

      const starter = getStarterFunc(Number(starterNumber))
      if (!starter) return defaultResult

      const fixedOddsInfo = starter.fixedOddsFinisherInfo
        ? starter.fixedOddsFinisherInfo
        : starter.fixedOddsStarterInfo
      const silkImage = starter.getSilkImage()

      result = {
        starterKey,
        name: starter.name(),
        isScratched: starter.isScratched(),
        isEmergency: starter.isEmergency(),
        emergencyNum: starter.emergencyNum(),
        hasBetSelected: false,
        scratchType: starter.scratchType(),
        silkImage: {
          cssurl: silkImage ? silkImage.getCssBackgroundFor(starter.number(), starter.type()) : '',
          url: '',
          width: silkImage ? silkImage.size.width : '',
        },
        fixedOdds: {
          win: fixedOddsInfo.displayWinDividend(),
          place: fixedOddsInfo.displayPlaceDividend(),
          isSuspended: fixedOddsInfo.isSuspended(),
          isScratched: fixedOddsInfo.isScratched(),
        },
        toteOdds: {
          win: starter.displayWinDividend(),
          place: starter.displayPlaceDividend(),
        },
      }

      if (Object.prototype.hasOwnProperty.call(starter, 'barrier')) {
        result.barrier = starter.barrier()
      }
      if (Object.prototype.hasOwnProperty.call(starter, 'boxNumber')) {
        result.boxNumber = starter.boxNumber()
      }
      if (Object.prototype.hasOwnProperty.call(starter, 'handicap')) {
        result.handicap = starter.handicap()
      }

      // Server API (RaceController.GetAcceptors) returns different results based on the race status
      // - open  --> returns RaceStarters
      // - other (jumped, resulted, abandoned, released, etc) --> returns Finishers.. which includes the 'placing' property used for determining 'hasJumped'
      result.hasJumped = Object.prototype.hasOwnProperty.call(starter, 'placing')

      return result
    },
    getRaceDetail: () => getRaceDetailFunc() || defaultRace,
    selectAcceptor: (acceptorNumber: number, isFixed: boolean) =>
      selectAcceptorFunc(acceptorNumber, isFixed),
    clearSelection: clearSelectionFunc,
    selectQuaddie: (raceNumbers: number[], acceptorNumbers: number[][]) => {
      selectQuaddieFunc(raceNumbers, acceptorNumbers)
    },
  }

  // @ts-expect-error Types need some work
  OverlayOpen(() => <GiddyUpPage raceInfo={raceInfo} />)
}

const defaultRace = {
  raceCourse: 'Missing',
  raceNumber: 1,
  distance: '0',
  trackCondition: '',
  weatherClass: '',
  raceStatus: '',
  raceTimeLocal: 0,
}

const defaultResult = {
  name: 'Missing information',
  number: 1,
  isEmergency: false,
  isScratched: false,
  hasBetSelected: false,
  scratchType: '',
  silkImage: { cssurl: '', url: '' },
  fixedOdds: { win: 0.0, isSuspended: false },
  toteOdds: { win: 0.0 },
}
